import { useMemo, useCallback, ChangeEvent, useEffect } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { MicrosoftVerifiedDomain, UnavailableDomain } from 'types/emailServer'
import { setMsDomainName, getDomainAvailability } from 'redux/features/emailServer/emailServerSlice'
import { trackEventInAllServices, TRACKING_EVENTS } from 'lib/monitoring/monitoringService'
import { isFailed, isSuccess } from 'redux/toolkit/api'

export interface State {
  msDomainName?: string
  verifiedDomains: MicrosoftVerifiedDomain[]
  availableDomains: string[]
  unavailableDomains: UnavailableDomain[]
}

export interface EventHandlers {
  onSelectChange: (e: ChangeEvent<HTMLInputElement>) => void
}

export type UseVerifyMsDomainLogic = [State, EventHandlers]

export const useVerifyMsDomainLogic = (): UseVerifyMsDomainLogic => {
  const dispatch = useAppDispatch()

  const {
    msDomainName,
    verifiedDomains,
    availableDomains,
    unavailableDomains,
    getDomainAvailabilitySuccess,
    getDomainAvailabilityFailed
  } = useAppSelector(_store => ({
    msDomainName: _store.emailServer.msDomainName,
    verifiedDomains: _store.emailServer.emailServerSettings?.microsoftVerifiedDomains,
    availableDomains: _store.emailServer.availableDomains,
    unavailableDomains: _store.emailServer.unavailableDomains,
    getDomainAvailabilitySuccess: isSuccess(_store.emailServer.getDomainAvailabilityApiStatus),
    getDomainAvailabilityFailed: isFailed(_store.emailServer.getDomainAvailabilityApiStatus)
  }))

  // init
  useEffect(
    () => {
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_3_START_MICROSOFT)
      if (!getDomainAvailabilitySuccess && !getDomainAvailabilityFailed) {
        dispatch(getDomainAvailability())
      }
    },
    // eslint-disable-next-line
    []
  )

  useEffect(() => {
    if (availableDomains.length > 0 && getDomainAvailabilitySuccess) {
      if (!msDomainName || !availableDomains.includes(msDomainName)) {
        dispatch(setMsDomainName(availableDomains[0]))
      }
    }
  }, [dispatch, getDomainAvailabilitySuccess, availableDomains, msDomainName])

  const onSelectChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(setMsDomainName(e.target.value))
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_3_SELECT_DOMAIN, { domain: e.target.value })
    },
    [dispatch]
  )

  return useMemo(
    () => [
      {
        msDomainName,
        verifiedDomains: verifiedDomains || [],
        availableDomains,
        unavailableDomains
      },
      {
        onSelectChange
      }
    ],
    [msDomainName, verifiedDomains, availableDomains, unavailableDomains, onSelectChange]
  )
}
